<script setup lang="ts">
import { ID_EURO_MILLIONS } from '~/utils/constants/idsLotteries.js'
import IconPlayStation from '~/components/icons/IconPlayStation.vue'
import IconArrowThinRight from '~/components/icons/IconArrowThinRight.vue'
import { trackRulesPlayClick, trackRulesOfficialClick } from '~/tracking/resultEvents'
import { SHOW_BUTTONS_MODAL_HOW_TO_PLAY } from '~/config/featureFlags'

defineOptions({
	name: 'ModalHowToPlay',
})

interface Props {
	modelValue?: boolean
	idLottery?: number
}

const props = withDefaults(defineProps<Props>(), {
	modelValue: false,
	idLottery: ID_EURO_MILLIONS,
})

const { t: $t } = useI18n()
const { lotteries: lotteriesRules } = useLotteriesRules()
const emit = defineEmits(['update:modelValue'])

const isOpen = computed({
	get() {
		return props.modelValue
	},
	set(value) {
		emit('update:modelValue', value)
	},
})

const getLotteryDetail = computed(() => {
	return lotteriesRules.find((lottery) => lottery.id === props.idLottery)
})

const classGridCols = computed(() => {
	const maxCols = 3
	const count = Math.max(1, Math.min(getLotteryDetail.value?.rules?.length ?? 1, maxCols))
	const map: Record<number, string> = {
		2: 'grid-cols-1 sm:grid-cols-2',
		3: 'grid-cols-1 md:grid-cols-3',
	}
	return map[count]
})

const classArrowRight = computed(() => {
	const maxCols = 3
	const count = Math.max(1, Math.min(getLotteryDetail.value?.rules?.length ?? 1, maxCols))
	const map: Record<number, string> = {
		2: 'hidden sm:block',
		3: 'hidden md:block',
	}
	return map[count]
})

const classContainerRule = computed(() => {
	const maxCols = 3
	const count = Math.max(1, Math.min(getLotteryDetail.value?.rules?.length ?? 1, maxCols))
	const map: Record<number, string> = {
		2: 'sm:flex-col',
		3: 'md:flex-col',
	}
	return map[count] || 'w-full'
})

const sendEventPlayClicked = () => {
	trackRulesPlayClick({
		lottery_id: props.idLottery ?? 0,
		game_name: getLotteryDetail.value?.name ?? '',
		link_url: getLotteryDetail.value?.link ?? '',
	})
}

const sendEventRulesOfficialClicked = () => {
	trackRulesOfficialClick({
		lottery_id: props.idLottery ?? 0,
		game_name: getLotteryDetail.value?.name ?? '',
		link_url: getLotteryDetail.value?.link_rules ?? '',
	})
}
</script>

<template>
	<UModal
		v-model="isOpen"
		class="modal-how-to-play"
		:ui="{
			overlay: {
				background: 'bg-[#040404D4] bg-opacity-80',
			},
			width: 'w-full sm:max-w-5xl',
			container: 'items-center',
			padding: 'p-5 sm:p-5',
		}"
	>
		<div class="flex flex-col gap-6 items-center justify-center px-6 sm:px-11 py-8 sm:py-11 bg-white rounded-lg">
			<!-- Header -->
			<div class="flex flex-col gap-9 sm:gap-12 items-center justify-center w-full">
				<div class="flex flex-col gap-11 items-center justify-center w-full">
					<div class="flex flex-col gap-4 items-center justify-center w-full">
						<!-- Modal Close Button -->
						<div class="flex items-center justify-between w-full">
							<div class="h-6" />
							<div class="flex gap-[21px] items-center">
								<UButton
									icon="i-heroicons-x-mark-20-solid"
									color="gray"
									variant="ghost"
									size="xl"
									class="p-2 w-8 h-8 flex justify-center items-center text-[#212529]"
									@click="isOpen = false"
								/>
							</div>
						</div>

						<div class="flex flex-col gap-6 items-start justify-start w-full">
							<!-- Badge -->
							<div>
								<UBadge
									color="primary"
									size="lg"
									class="text-[18px] font-bold px-3 py-1 rounded-full"
								>
									{{ getLotteryDetail?.id === ID_EURO_MILLIONS ? $t(getLotteryDetail?.name) : getLotteryDetail?.name }}
								</UBadge>
							</div>

							<!-- Description -->
							<p class="text-neutral-800 text-sm sm:text-lg leading-6 sm:leading-7 font-normal">
								{{ getLotteryDetail?.description }}.
							</p>

							<!-- Divider -->
							<UDivider class="border-[#18181B]/[0.6]" />

							<!-- How to Play Header -->
							<div class="flex gap-4 items-center">
								<IconPlayStation class="text-primary-600" />

								<h2 class="text-[#212529] text-base sm:text-lg font-bold leading-7">{{ $t('How to play') }}</h2>
							</div>
						</div>
					</div>
				</div>

				<!-- Steps -->
				<div class="border border-[#c4c4c4] rounded-md w-full">
					<div
						class="grid divide-y md:divide-y-0"
						:class="classGridCols"
					>
						<div
							v-for="(rule, index) in getLotteryDetail?.rules"
							:key="index"
							class="flex relative"
						>
							<IconArrowThinRight
								v-if="index !== 0"
								class="h-full w-full text-[#CFD6DC] shrink-0 absolute right-1/2"
								:class="classArrowRight"
							/>
							<div
								class="flex xl:flex-row gap-4 items-center p-6"
								:class="classContainerRule"
							>
								<div
									class="bg-primary-50 size-10 rounded-full flex items-center justify-center border-2 border-primary shrink-0"
								>
									<span class="text-primary text-base font-medium">{{ String(index + 1).padStart(2, '0') }}</span>
								</div>
								<p class="text-neutral-700 text-sm font-medium leading-5">{{ rule }}.</p>
							</div>
						</div>
					</div>
				</div>

				<!-- Footer -->
				<template v-if="$hasFeature(SHOW_BUTTONS_MODAL_HOW_TO_PLAY) === true">
					<UDivider />
					<div class="flex flex-col sm:flex-row justify-end gap-6 sm:gap-2 w-full">
						<UButton
							:to="getLotteryDetail?.link_rules"
							target="_blank"
							variant="ghost"
							color="gray"
							class="h-10 font-medium text-neutral-900 w-full sm:w-auto flex justify-center rounded-full"
							trailing-icon="ph:arrow-square-out"
							@click="sendEventRulesOfficialClicked"
						>
							{{ $t('View official rules') }}
						</UButton>
						<UButton
							:to="getLotteryDetail?.link"
							target="_blank"
							variant="solid"
							color="primary"
							class="h-10 px-6 rounded-full font-semibold min-w-44 w-full sm:w-auto flex justify-center"
							@click="sendEventPlayClicked"
						>
							{{ $t('Play now') }}
						</UButton>
					</div>
				</template>
			</div>
		</div>
	</UModal>
</template>
